import { alpha, Backdrop, ThemeProvider } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useBeforeUnload } from 'react-router-dom'
import { getLastPosition } from '../../../chess/gameTree'
import AppLayout from '../../../components/AppLayout/AppLayout'
import { PuzzleStreakOverlay, StreakAnimation } from '../../../components/PuzzleStreakOverlay/PuzzleStreakOverlay'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import { LoginState } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { useScreenSize } from '../../../sharedComponents/src/hooks/useScreenSize'
import theme from '../../../sharedComponents/src/Theme/theme-v2'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import PuzzlesChessBoard from '../../chessboard/PuzzlesChessboard'
import FinishFtueDialog from '../../PuzzlesView/components/FinishFtueDialog'
import HintLimitDialog from '../../PuzzlesView/components/HintLimitDialog'
import PuzzlesLimitDialog from '../../PuzzlesView/components/v2/PuzzleLimitDialog'
import useResultSounds from '../../PuzzlesView/hooks/useResultSounds'
import PuzzlePracticeCharacter from './components/PuzzlePracticeCharacter'
import PuzzlePracticeTitle from './components/PuzzlePracticeTitle'
import StreaksAndRating from './components/StreaksAndRating'
import DesktopPP from './layouts/DesktopPP'
import MobilePP from './layouts/MobilePP'

const PuzzlePracticeView: React.FC = () => {
    const userState = useStoreState((state) => state.userData.state)
    const userGroups = useStoreState((state) => state.userData.userData?.groups)
    const userData = useStoreState((state) => state.userData.userData)
    const settings = useStoreState((state) => state.gameView.settings)

    const gameTree = useStoreState((state) => state.puzzlePracticeView.gameTree)
    const currentPosition = useStoreState((state) => state.puzzlePracticeView.currentPosition)
    const flipped = useStoreState((state) => state.puzzlePracticeView.flipped)
    const myColor = useStoreState((state) => state.puzzlePracticeView.myColor)
    const hint = useStoreState((state) => state.puzzlePracticeView.hint)
    const customArrows = useStoreState((state) => state.puzzlePracticeView.customArrows)
    const legalMove = useStoreState((state) => state.puzzlePracticeView.legalMove)
    const puzzleState = useStoreState((state) => state.puzzlePracticeView.puzzleState)
    const showSolution = useStoreState((state) => state.puzzlePracticeView.showSolution)
    const usedShowSolution = useStoreState((state) => state.puzzlePracticeView.usedShowSolution)
    const morePuzzlesOpen = useStoreState((state) => state.puzzlePracticeView.morePuzzlesOpen)
    const joinUsOpen = useStoreState((state) => state.puzzlePracticeView.joinUsOpen)
    const showHintLimitDialog = useStoreState((state) => state.puzzlePracticeView.showHintLimitDialog)
    const showFinishFtueDialog = useStoreState((state) => state.puzzlePracticeView.showFinishFtueDialog)
    const streaks = useStoreState((state) => state.puzzlePracticeView.streaks)
    const showSteakAnimation = useStoreState((state) => state.puzzlePracticeView.showSteakAnimation)
    const attemptsCounter = useStoreState((state) => state.puzzlePracticeView.attemptsCounter)
    const showBackdrop = useStoreState((state) => state.puzzlePracticeView.showBackdrop)
    const ftueCounter = useStoreState((state) => state.puzzlePracticeView.ftueCounter)

    const handleMove = useStoreActions((actions) => actions.puzzlePracticeView.handleMove)
    const getTags = useStoreActions((actions) => actions.puzzlePracticeView.getTags)
    const getPuzzle = useStoreActions((actions) => actions.puzzlePracticeView.getPuzzle)
    const setMorePuzzlesOpen = useStoreActions((actions) => actions.puzzlePracticeView.setMorePuzzlesOpen)
    const setJoinUsOpen = useStoreActions((actions) => actions.puzzlePracticeView.setJoinUsOpen)
    const setShowHintLimitDialog = useStoreActions((actions) => actions.puzzlePracticeView.setShowHintLimitDialog)
    const setShowFinishFtueDialog = useStoreActions((actions) => actions.puzzlePracticeView.setShowFinishFtueDialog)
    const setShowStreakAnimation = useStoreActions((actions) => actions.puzzlePracticeView.setShowSteakAnimation)
    const setStreaks = useStoreActions((actions) => actions.puzzlePracticeView.setStreaks)
    const sendPuzzle = useStoreActions((actions) => actions.puzzlePracticeView.sendPuzzle)
    const setShowHintLimit = useStoreActions((actions) => actions.puzzlePracticeView.setShowHintLimit)
    const handleFtueStep = useStoreActions((actions) => actions.puzzlePracticeView.handleFtueStep)
    const resetAllStates = useStoreActions((actions) => actions.puzzlePracticeView.resetAllStates)

    const { isMobile } = useScreenSize()
    const playResultSound = useResultSounds(settings)
    const { registerDialog, unregisterDialog } = useDialogs()
    const disableByMoveList = useMemo(() => currentPosition.id !== getLastPosition(gameTree).id, [currentPosition.id])

    // SAVE PROGRESS BEFORE RELOAD THE PAGE
    const handleBeforeUnload = (event) => {
        event.preventDefault()
        puzzleState !== true && attemptsCounter > 1 && sendPuzzle()
    }
    useBeforeUnload(handleBeforeUnload)

    // HIDE HINT LIMIT MESSAGE
    const handleClickAnywhere = () => {
        setShowHintLimit(false)
    }
    useEffect(() => {
        document.addEventListener('mousedown', handleClickAnywhere)
        return () => {
            document.removeEventListener('mousedown', handleClickAnywhere)
            resetAllStates()
            unregisterDialog(Dialogs.PUZZLE_LIMIT)
            unregisterDialog(Dialogs.HINT_LIMIT)
            unregisterDialog(Dialogs.FINISH_PUZZLES_FTUE)
        }
    }, [])

    // REGISTER PUZZLE LIMIT DIALOG
    useEffect(() => {
        if (morePuzzlesOpen) {
            registerDialog(
                Dialogs.PUZZLE_LIMIT,
                <PuzzlesLimitDialog variant="puzzlePracticePremium" onClose={() => setMorePuzzlesOpen(false)} />,
            )
        } else if (joinUsOpen) {
            registerDialog(
                Dialogs.PUZZLE_LIMIT,
                <PuzzlesLimitDialog variant="puzzlePracticeMember" onClose={() => setJoinUsOpen(false)} />,
            )
        } else {
            unregisterDialog(Dialogs.PUZZLE_LIMIT)
        }
        // return () => {
        //     unregisterDialog(Dialogs.PUZZLE_LIMIT)
        // }
    }, [morePuzzlesOpen, joinUsOpen])

    // REGISTER HINT LIMIT DIALOG
    useEffect(() => {
        if (showHintLimitDialog) {
            setShowHintLimitDialog(false)
            registerDialog(Dialogs.HINT_LIMIT, <HintLimitDialog />)
        }
        // return () => {
        //     setShowHintLimitDialog(false)
        //     unregisterDialog(Dialogs.HINT_LIMIT)
        // }
    }, [showHintLimitDialog])

    // REGISTER FINISH FTUE DIALOG
    useEffect(() => {
        if (showFinishFtueDialog) {
            setShowFinishFtueDialog(false)
            registerDialog(Dialogs.FINISH_PUZZLES_FTUE, <FinishFtueDialog />)
        }
        // return () => {
        //     setShowFinishFtueDialog(false)
        //     unregisterDialog(Dialogs.FINISH_PUZZLES_FTUE)
        // }
    }, [showFinishFtueDialog])

    // LOAD PUZZLE DATA
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (userState === LoginState.LOGGED_IN) {
                setJoinUsOpen(false)
                resetAllStates()
                getTags()
                getPuzzle()
            } else {
                setJoinUsOpen(true)
                resetAllStates()
            }
        }, 1)
        return () => {
            clearTimeout(timeout)
        }
    }, [userState, userGroups, userData?.user_name])

    // RESULT SOUND AND UPDATE STREAKS
    useEffect(() => {
        if (puzzleState !== null) {
            // Play result sound
            puzzleState ? playResultSound(true) : playResultSound(false)

            // Update streaks
            const newStreak = puzzleState && attemptsCounter <= 1 && !usedShowSolution ? streaks.current + 1 : 0
            if (streaks.current === streaks.best && !newStreak && streaks.best) {
                setShowStreakAnimation(true)
            } else {
                setStreaks({
                    current: newStreak,
                    best: newStreak > streaks.best ? newStreak : streaks.best,
                    updatedAt: new Date().toISOString(),
                })
            }
        }
    }, [puzzleState])

    // HANDLE FTUE STEP
    useEffect(() => {
        handleFtueStep()
    }, [ftueCounter, showBackdrop])

    const handleAnimationEnd = () => {
        setStreaks({
            ...streaks,
            current: 0,
            updatedAt: new Date().toISOString(),
        })
        setShowStreakAnimation(false)
    }

    return (
        <AppLayout
            compactChild
            aboveBoard={
                !isMobile ? undefined : (
                    <ThemeProvider theme={theme}>
                        {/* Title */}
                        <PuzzlePracticeTitle />
                        {/* Streaks and Rating */}
                        <StreaksAndRating />
                        {/* Character */}
                        <PuzzlePracticeCharacter />
                    </ThemeProvider>
                )
            }
            board={
                <>
                    {showSteakAnimation && (
                        <PuzzleStreakOverlay
                            value={streaks.best}
                            variant={StreakAnimation.PRACTICE}
                            onAnimationEnd={handleAnimationEnd}
                        />
                    )}
                    <PuzzlesChessBoard
                        mode="puzzle"
                        gameTree={gameTree}
                        currentPositionId={currentPosition.id}
                        flipped={flipped}
                        onMove={handleMove}
                        myColor={myColor}
                        hint={hint}
                        legalMove={legalMove}
                        customArrows={customArrows}
                        disableBoard={currentPosition.position.turn !== myColor || showSolution || disableByMoveList}
                        moveStatus={puzzleState === null ? undefined : puzzleState}
                    />
                </>
            }
        >
            <ThemeProvider theme={theme}>{isMobile ? <MobilePP /> : <DesktopPP />}</ThemeProvider>
            <Backdrop
                sx={(theme) => ({
                    backgroundColor: alpha(theme.palette.background.default, 0.85),
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={showBackdrop}
            />
        </AppLayout>
    )
}

export default PuzzlePracticeView
